import { IconClipboardCheck } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import {
  getGetSiteInspectionSettingsQueryKey,
  useGetSiteInspectionSettings,
  useUpdateSiteInspectionSettings,
} from "@/api/client-inspection";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/hooks/use-auth";
import { useSelectedCTESiteId } from "@/stores/site-store";

export const Route = createFileRoute("/dashboard/workspace/inspections")({
  component: InspectionSettingsPage,
  staticData: {
    title: "Inspections",
  },
});

/**
 * How the selected site runs its inspections.
 *
 * Per-site rather than per-account: this decides how a customer's compliance
 * work is signed off, so it belongs to the site the user is looking at, not to
 * the person looking at it.
 */
function InspectionSettingsPage() {
  const siteId = useSelectedCTESiteId();
  const { canAccess } = useAuth();
  const queryClient = useQueryClient();

  const canChange = canAccess("manager", siteId);

  const { data: settings, isLoading } = useGetSiteInspectionSettings(
    siteId ?? "",
    { query: { enabled: !!siteId } },
  );

  const update = useUpdateSiteInspectionSettings({
    mutation: {
      onSuccess: () => {
        toast.success("Inspection settings saved");
        if (siteId) {
          void queryClient.invalidateQueries({
            queryKey: getGetSiteInspectionSettingsQueryKey(siteId),
          });
        }
      },
      onError: (error) =>
        toast.error(error.message ?? "Could not save the settings"),
    },
  });

  if (!siteId) {
    return (
      <p className="text-muted-foreground text-sm">
        Choose a site to see how its inspections are run.
      </p>
    );
  }

  const requireApproval = settings?.requireManagerApproval ?? false;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold tracking-tight">Inspections</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          How work at this site is signed off.
        </p>
      </div>

      <Card className="p-6">
        <label className="flex cursor-pointer items-start gap-3">
          <Checkbox
            className="mt-0.5"
            checked={requireApproval}
            disabled={!canChange || isLoading || update.isPending}
            onCheckedChange={(checked) =>
              update.mutate({
                siteId,
                data: { requireManagerApproval: checked === true },
              })
            }
          />
          <span className="min-w-0">
            <span className="flex items-center gap-2 text-sm font-medium">
              <IconClipboardCheck className="text-muted-foreground size-4 shrink-0" />
              A manager approves every completed inspection
            </span>
            <span className="text-muted-foreground mt-1 block text-sm">
              The inspector finishes the inspection; a manager then checks the
              evidence and approves it, or sends it back with a reason. Until a
              manager approves it, the inspection is not final and the register
              does not record its values as verified.
            </span>
            <span className="text-muted-foreground mt-2 block text-sm">
              Only inspections completed after this change are affected. Work
              already finished stays finished, and anything already waiting on a
              manager keeps waiting.
            </span>
          </span>
        </label>

        {!canChange && (
          <p className="text-muted-foreground mt-4 text-sm">
            A manager or an administrator can change this.
          </p>
        )}
      </Card>
    </div>
  );
}
